import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Mail, Instagram, CheckCircle2, Download, BookOpen, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import QuoteForm from "../components/QuoteForm";
import CatalogueViewer, { TOTAL_PAGES, pageUrl, PDF_URL, SOURCES } from "../components/CatalogueViewer";
import LeadMagnetModal from "../components/LeadMagnetModal";
import HingeLoadCalculator from "../components/HingeLoadCalculator";
import QuickOrderTable from "../components/QuickOrderTable";

const HERO = "/brand/ikel-hinge-promo-full.jpg";

// Featured preview pages spread across the catalogue (cover + category pages)
const FEATURED_PAGES = [1, 12, 24, 40, 58, 78, 98, 120];

export default function IkelMt() {
  const { t } = useLang();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerStart, setViewerStart] = useState(1);
  const [viewerSource, setViewerSource] = useState(SOURCES.ikel);
  const [leadOpen, setLeadOpen] = useState(false);

  const openViewer = (page = 1, src = SOURCES.ikel) => {
    setViewerSource(src);
    setViewerStart(page);
    setViewerOpen(true);
  };

  return (
    <div data-testid="ikel-page">
      <CatalogueViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        initialPage={viewerStart}
        source={viewerSource}
      />
      <LeadMagnetModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        fileHref={PDF_URL}
        fileName="ikel-mt-catalogue.pdf"
      />
      {/* Hero */}
      <section className="relative bg-[#0B1B29] text-white overflow-hidden">
        <img
          src={HERO}
          alt="Furniture hardware macro"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative pgb-container py-24 md:py-32 max-w-4xl">
          <img
            src="/brand/ikel-mt-logo.png"
            alt="İKEL MT"
            className="h-16 md:h-20 w-auto mb-8 bg-white/95 p-3 pr-5 inline-block"
          />
          <span className="eyebrow text-[#F59E0B]">{t.ikelPage.eyebrow}</span>
          <h1 className="mt-5 text-4xl md:text-6xl font-black tracking-tighter leading-[1.02]">
            {t.ikelPage.title}
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">
            {t.ikelPage.subtitle}
          </p>
          <div className="mt-8 inline-flex items-center gap-3 border border-white/20 px-5 py-3">
            <span className="text-3xl font-black text-[#F59E0B]" style={{ fontFamily: "Chivo" }}>5+</span>
            <span className="text-sm text-white/80 tracking-wide">{t.ikelPage.experienceBadge}</span>
          </div>
          <div className="mt-10">
            <Link to="/quote" className="btn-accent" data-testid="ikel-hero-cta">
              {t.common.requestQuote} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Catalogue preview — real pages from the PDF */}
      <section className="py-24 md:py-32 bg-white" data-testid="catalogue-preview-section">
        <div className="pgb-container">
          <div className="flex items-end justify-between flex-wrap gap-8 mb-12">
            <div className="max-w-2xl">
              <span className="eyebrow">{t.common.catalogue.eyebrow}</span>
              <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
                {t.common.catalogue.title}
              </h2>
              <p className="mt-5 text-[#536878] leading-relaxed">
                {t.common.catalogue.subtitle}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => openViewer(1)}
                className="btn-primary"
                data-testid="catalogue-browse-all"
              >
                <BookOpen size={16} /> {t.common.catalogue.browseAll}
              </button>
              <a
                href={PDF_URL}
                download
                onClick={(e) => { e.preventDefault(); setLeadOpen(true); }}
                className="btn-secondary"
                data-testid="catalogue-download"
              >
                <Download size={16} /> {t.common.catalogue.download}
              </a>
              <Link
                to="/quote?from=catalogue&page=1"
                className="btn-accent"
                data-testid="catalogue-quote-link"
              >
                {t.common.requestQuote} <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {FEATURED_PAGES.map((n) => (
              <button
                key={`feat-${n}`}
                type="button"
                onClick={() => openViewer(n)}
                className="group relative overflow-hidden bg-[#F5F5F7] border border-[#E2E8F0] hover:border-[#0B1B29] transition-colors"
                data-testid={`catalogue-featured-${n}`}
                aria-label={`Open catalogue at page ${n}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={pageUrl(n)}
                    alt={`İKEL MT catalogue page ${n}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-[#0B1B29]/0 group-hover:bg-[#0B1B29]/40 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold tracking-[0.18em] uppercase border border-white px-4 py-2">
                    {t.common.catalogue.page} {n}
                  </span>
                </div>
                <div className="absolute top-2 left-2 bg-white/95 text-[#0B1B29] text-[10px] font-mono tracking-wider uppercase px-2 py-0.5">
                  {String(n).padStart(3, "0")} / {TOTAL_PAGES}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Catalog categories (supporting info) */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="pgb-container">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <span className="eyebrow">{t.ikelPage.catalogTitle}</span>
              <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
                {t.trio.ikel.title}
              </h2>
            </div>
            <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#536878]">
              06 / categories
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {t.ikelPage.categories.map((cat, idx) => (
              <div
                key={cat.name}
                className="bg-white p-8 md:p-10 flex flex-col gap-3 hover:bg-[#0B1B29] hover:text-white transition-colors group"
                data-testid={`ikel-cat-${idx}`}
              >
                <span className="font-mono text-xs tracking-[0.18em] text-[#1D4ED8] group-hover:text-[#93C5FD]">
                  / {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="text-xl font-bold tracking-tight">{cat.name}</h3>
                <p className="text-sm text-[#536878] group-hover:text-white/75 leading-relaxed">
                  {cat.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guibang — Official Distributor */}
      <section
        className="py-24 md:py-32 bg-white relative overflow-hidden"
        data-testid="guibang-section"
      >
        <div className="absolute inset-0 pointer-events-none opacity-50">
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-[#93C5FD]/25 blur-[160px]" />
        </div>
        <div className="relative pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 bg-[#0B1B29] text-[#F59E0B] text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1.5 mb-6">
              <ShieldCheck size={13} /> {t.common.guibang.badge}
            </div>
            <span className="eyebrow">{t.common.guibang.eyebrow}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29] leading-[1.05]">
              {t.common.guibang.title}
            </h2>
            <p className="mt-6 text-[#0B1B29]/80 leading-relaxed text-base md:text-lg">
              {t.common.guibang.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <div className="bg-white border border-[#E2E8F0] p-5 inline-flex items-center gap-4">
                <img
                  src="/brand/ikel-mt-logo.png"
                  alt="İKEL MT"
                  className="h-8 w-auto"
                />
                <span className="text-2xl text-[#536878]">×</span>
                <img
                  src="/brand/guibang-logo.png"
                  alt="Guibang"
                  className="h-8 w-auto"
                />
              </div>
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-[#536878]">
                {t.common.guibang.partner}
              </span>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => openViewer(1, SOURCES.guibang)}
                className="btn-primary"
                data-testid="guibang-browse-btn"
              >
                <BookOpen size={16} /> {t.common.guibang.browse}
              </button>
              <Link
                to="/quote?from=catalogue&brand=Guibang&page=1"
                className="btn-secondary"
                data-testid="guibang-quote-link"
              >
                {t.common.requestQuote} <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={`gb-${n}`}
                  type="button"
                  onClick={() => openViewer(n, SOURCES.guibang)}
                  className="group relative overflow-hidden border border-[#E2E8F0] hover:border-[#0B1B29] transition-colors aspect-[3/4]"
                  data-testid={`guibang-preview-${n}`}
                  aria-label={`Open Guibang catalogue page ${n}`}
                >
                  <img
                    src={`/catalogue/guibang/pages/p-${String(n).padStart(3, "0")}.jpg`}
                    alt={`Guibang catalogue page ${n}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#0B1B29]/0 group-hover:bg-[#0B1B29]/40 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] font-semibold tracking-[0.18em] uppercase border border-white px-3 py-1.5">
                      {t.common.catalogue.page} {n}
                    </span>
                  </div>
                  <div className="absolute top-2 left-2 bg-white/95 text-[#0B1B29] text-[10px] font-mono tracking-wider uppercase px-2 py-0.5">
                    {String(n).padStart(3, "0")} / 58
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 text-xs font-mono tracking-[0.15em] uppercase text-[#536878] flex items-center gap-2">
              <BookOpen size={12} /> {t.common.guibang.catalogueTitle} · {t.common.guibang.catalogueDesc}
            </div>
          </div>
        </div>
      </section>

      {/* Hinge Load Calculator — Spec Finder */}
      <HingeLoadCalculator />

      {/* Quick Order table — digital catalog filter */}
      <QuickOrderTable />

      {/* Retail store — KEL MEBEL AKSESUARLARI */}
      <section className="py-24 md:py-32 bg-[#0B1B29] text-white relative overflow-hidden" data-testid="ikel-store-section">
        <div className="absolute inset-0 pointer-events-none opacity-20">
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] bg-[#F59E0B] blur-[180px] opacity-30" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] bg-[#1D4ED8] blur-[180px] opacity-40" />
        </div>
        <div className="relative pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <span className="eyebrow text-[#F59E0B]">{t.ikelPage.store.eyebrow}</span>
            <h2 className="mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[1.02]">
              {t.ikelPage.store.brand}
            </h2>
            <p className="mt-6 text-xl md:text-2xl text-[#F59E0B] font-bold tracking-tight leading-snug">
              {t.ikelPage.store.tagline}
            </p>
            <p className="mt-5 text-white/75 leading-relaxed max-w-xl">
              {t.ikelPage.store.subtitle}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 border border-[#F59E0B]/40 text-[#F59E0B] px-4 py-2 text-xs font-semibold tracking-wider uppercase">
                <CheckCircle2 size={14} /> {t.ikelPage.store.stockBadge}
              </span>
              <span className="inline-flex items-center gap-2 border border-white/25 text-white/85 px-4 py-2 text-xs font-semibold tracking-wider uppercase">
                <CheckCircle2 size={14} /> {t.ikelPage.store.customBadge}
              </span>
            </div>
          </div>

          <div className="lg:col-span-6 border border-white/15 p-8 md:p-10 bg-white/[0.03] backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-white p-2 inline-block">
                <img src="/brand/ikel-mt-logo.png" alt="İKEL MT" className="h-10 w-auto" />
              </div>
              <div className="h-10 w-px bg-white/20" />
              <div className="font-black tracking-tight text-white text-sm">
                KEL MEBEL<br /><span className="text-[#F59E0B]">AKSESUARLARI</span>
              </div>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#F59E0B] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Location</div>
                  <div className="mt-1 text-white font-semibold">{t.ikelPage.store.addressLine1}</div>
                  <div className="text-white/75 text-sm">{t.ikelPage.store.addressLine2}</div>
                  <div className="text-white/60 text-sm mt-0.5">{t.ikelPage.store.city}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail size={18} className="text-[#F59E0B] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">{t.ikelPage.store.emailLabel}</div>
                  <a
                    href={`mailto:${t.footer.emailIkel}`}
                    className="mt-1 text-white link-underline break-all block"
                    data-testid="ikel-store-email"
                  >
                    {t.footer.emailIkel}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Instagram size={18} className="text-[#F59E0B] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Instagram</div>
                  <a
                    href={t.footer.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-white link-underline block"
                    data-testid="ikel-store-instagram"
                  >
                    {t.footer.social.instagramHandle}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-[#F59E0B] mt-1 shrink-0">
                  <path d="M19.6 6.4a5.3 5.3 0 0 1-3.2-3.2h-3v13.3a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .7.1v-3a5.6 5.6 0 1 0 4.9 5.5V9.7a8.2 8.2 0 0 0 4.8 1.5V8.1a5.2 5.2 0 0 1-1.6-.3c-.4-.1-.7-.3-1-.5Z" />
                </svg>
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">TikTok</div>
                  <a
                    href={t.footer.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-white link-underline block"
                    data-testid="ikel-store-tiktok"
                  >
                    {t.footer.social.tiktokHandle}
                  </a>
                </div>
              </div>
            </div>

            <a
              href="https://www.google.com/maps/search/?api=1&query=Binəqədi+Şosesi+9+Baku+Azpetrol"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent mt-8 w-full justify-center bg-[#F59E0B] hover:bg-white hover:text-[#0B1B29]"
              data-testid="ikel-store-cta"
            >
              {t.ikelPage.store.visitCta} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA + Form */}
      <section className="py-24 md:py-32 bg-white">
        <div className="pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t.common.requestQuote}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              {t.ikelPage.ctaTitle}
            </h2>
            <p className="mt-5 text-[#536878] leading-relaxed max-w-md">
              {t.ikelPage.ctaDesc}
            </p>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm defaultProduct="hardware" />
          </div>
        </div>
      </section>
    </div>
  );
}
